import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users,
  Clapperboard,
  Star,
  TrendingUp,
  MoreVertical,
  Plus,
  Eye,
  Film,
  Video
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./DirectorDashboard.css";
import { fetchCastingDirectorDashboardApi } from "../../../services/api";

const DirectorDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const filteredApplications =
    activeTab === "all"
      ? dashboardData?.recentApplications || []
      : dashboardData?.recentApplications?.filter((item) => {
          const status = String(item?.status || '').toLowerCase();
          if (activeTab === "pending") {
            return status === "pending" || status === "applied";
          }
          if (activeTab === "shortlisted") {
            return (
              status === "shortlisted" ||
              status === "selected"
            );
          }
          return true;
        }) || [];

  const fetchDashboard = async () => {
    try {
      const response = await fetchCastingDirectorDashboardApi();
      setDashboardData(response.data.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status = '') => {
    const s = String(status || '').toLowerCase();
    if (s === "pending" || s === "applied") return <span className="dd-badge badge-yellow">PENDING</span>;
    if (s === "resubmit") return <span className="dd-badge" style={{ background: "rgba(168, 85, 247, 0.15)", color: "#c084fc" }}>RESUBMIT</span>;
    if (s === "reviewed") return <span className="dd-badge badge-blue">REVIEWED</span>;
    if (s === "shortlisted" || s === "selected") return <span className="dd-badge badge-green">SHORTLISTED</span>;
    if (s === "rejected") return <span className="dd-badge badge-red">REJECTED</span>;
    return <span className="dd-badge badge-gray">{String(status || 'PENDING').toUpperCase()}</span>;
  };

  const getTypeBadge = (type = '') => {
    const t = String(type || '').toUpperCase();
    if (t.includes("ONLINE")) return <span className="dd-type-badge type-online"><Video size={12}/> ONLINE</span>;
    if (t.includes("SLOT_BOOKING") || t.includes("WALK")) return <span className="dd-type-badge type-walkin">WALK IN</span>;
    return <span className="dd-type-badge type-gray">{t || 'ONLINE'}</span>;
  };

  const COLOR_MAP = {
    Shortlisted: "#00c781",
    Selected: "#00c781",
    Pending: "#ffb020",
    Applied: "#ffb020",
    Resubmit: "#a855f7",
    RESUBMIT: "#a855f7",
    Rejected: "#ff4d4f"
  };

  const pieChartData = (dashboardData?.chartData || []).filter(
    (item) => item.value > 0
  );

  const totalApplicantsCount = (dashboardData?.chartData || []).reduce(
    (acc, curr) => acc + (Number(curr.value) || 0), 
    0
  );

  return (
    <div className="dd-wrapper">
      <div className="dd-container">
        
        {/* HEADER */}
        <div className="dd-header">
          <div>
            <h1 className="dd-title">Director Dashboard</h1>
            <p className="dd-subtitle">Manage your vision. Review applicants and active roles seamlessly.</p>
          </div>
          <button className="dd-btn-primary" onClick={() => navigate('/dashboard/post-roles')}>
            <Plus size={18} /> Post New Role
          </button>
        </div>

        {/* KPI CARDS */}
        <div className="dd-kpi-grid">
          {[
            {
              title: "Total Projects",
              value: dashboardData?.kpis?.totalProjects || 0,
              icon: <Clapperboard />,
              trend: "+1 this month"
            },
            {
              title: "Active Roles",
              value: dashboardData?.kpis?.activeRoles || 0,
              icon: <Film />,
              trend: "Active"
            },
            {
              title: "Total Applicants",
              value: dashboardData?.kpis?.totalApplicants || 0,
              icon: <Users />,
              trend: "+20%"
            },
            {
              title: "Shortlisted",
              value: dashboardData?.kpis?.shortlisted || 0,
              icon: <Star />,
              trend: "Selected"
            }
          ].map((kpi, index) => (
            <div className="dd-card dd-kpi-card" key={index}>
              <div className="dd-kpi-top">
                <div className="dd-kpi-icon">{kpi.icon}</div>
                <div className="dd-kpi-trend">
                  <TrendingUp size={14} /> <span>{kpi.trend}</span>
                </div>
              </div>
              <h3 className="dd-kpi-value">{kpi.value}</h3>
              <p className="dd-kpi-label">{kpi.title}</p>
            </div>
          ))}
        </div>

        {/* MIDDLE SECTION: CHART & ACTIVE ROLES */}
        <div className="dd-middle-grid">
          
          {/* APPLICANT CHART (Sleek Donut Chart) */}
          <div className="dd-card dd-chart-card">
            <div className="dd-card-header">
              <h2>Applicants Overview</h2>
              <select className="dd-select">
                <option>Last 7 Days</option>
                <option>This Month</option>
              </select>
            </div>
            
            <div 
              className="dd-chart-container" 
              style={{ position: "relative", width: "100%", height: "220px", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0];
                        const fillColor = COLOR_MAP[data.name] || "#3b82f6";
                        return (
                          <div style={{ background: "rgba(15, 15, 15, 0.95)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "12px", padding: "8px 14px", boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: fillColor }}></span>
                              <p style={{ margin: 0, color: "#fff", fontSize: "12px", fontWeight: 600 }}>{data.name}</p>
                            </div>
                            <p style={{ margin: "4px 0 0 0", color: "#9ca3af", fontSize: "11px" }}>
                              Count: <span style={{ color: "#fff", fontWeight: 700 }}>{data.value}</span>
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />

                  <Pie
                    data={pieChartData.length > 0 ? pieChartData : [{ name: "No Data", value: 1 }]}
                    cx="50%"
                    cy="50%"
                    innerRadius={68}
                    outerRadius={92}
                    paddingAngle={4}
                    dataKey="value"
                    nameKey="name"
                    cornerRadius={5}
                    stroke="none"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLOR_MAP[entry.name] || "#3B82F6"}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              {/* Centered Total Count Overlay */}
              <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none" }}>
                <span style={{ fontSize: "24px", fontWeight: "800", color: "#ffffff", display: "block", lineHeight: 1 }}>{totalApplicantsCount}</span>
                <span style={{ fontSize: "10px", fontWeight: "600", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.8px", marginTop: "4px", display: "block" }}>Total</span>
              </div>
            </div>

            {/* Custom Stat Legend Pills */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "14px", marginTop: "12px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#ffb020" }}></span>
                <span style={{ fontSize: "12px", color: "#9ca3af", fontWeight: "500" }}>Pending</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#a855f7" }}></span>
                <span style={{ fontSize: "12px", color: "#9ca3af", fontWeight: "500" }}>Resubmit</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#00c781" }}></span>
                <span style={{ fontSize: "12px", color: "#9ca3af", fontWeight: "500" }}>Shortlisted</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#ff4d4f" }}></span>
                <span style={{ fontSize: "12px", color: "#9ca3af", fontWeight: "500" }}>Rejected</span>
              </div>
            </div>
          </div>

          {/* ACTIVE ROLES LIST */}
          <div className="dd-card dd-roles-card">
            <div className="dd-card-header">
              <h2>My Active Roles</h2>
              <Link to="/dashboard/roles" className="dd-link-btn">View All</Link>
            </div>
            <div className="dd-roles-list">
              {dashboardData?.activeRoles?.map((role) => (
                <div className="dd-role-item" key={role.id}>
                  <div className="dd-role-info">
                    <div className="dd-role-icon-small"><Film size={16} /></div>
                    <div>
                      <h4 className="dd-role-title">{role.title}</h4>
                      <p className="dd-role-project">{role.project}</p>
                    </div>
                  </div>
                  <div className="dd-role-stats">
                    <div className="dd-applicant-count">
                      <strong>{role.applicants}</strong>
                      <span>Applicants</span>
                    </div>
                    {/* ✅ Three Dots Navigation to Edit particular role */}
                    <button 
                      className="dd-icon-btn" 
                      title="Edit Role"
                      onClick={() => navigate(`/dashboard/post-roles/${role.id}`)}
                    >
                      <MoreVertical size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* BOTTOM SECTION: RECENT AUDITIONS TABLE */}
        <div className="dd-card dd-table-card">
          <div className="dd-card-header">
            <h2>Recent Auditions</h2>
            <div className="dd-table-filters">
              <button className={activeTab === "all" ? "active" : ""} onClick={() => setActiveTab("all")}>All</button>
              <button className={activeTab === "pending" ? "active" : ""} onClick={() => setActiveTab("pending")}>Pending</button>
              <button className={activeTab === "shortlisted" ? "active" : ""} onClick={() => setActiveTab("shortlisted")}>Shortlisted</button>
            </div>
          </div>

          <div className="dd-table-responsive">
            <table className="dd-table">
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>CANDIDATE NAME</th>
                  <th>ROLE TITLE</th>
                  <th>PROJECT</th>
                  <th>TYPE</th>
                  <th>STATUS</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map((row) => (
                  <tr key={row.id || row.roleApplicationId}>
                    <td className="dd-text-muted">{row.date}</td>
                    <td>
                      <div className="dd-candidate-cell">
                        <div className="dd-avatar">{row.candidate ? String(row.candidate).charAt(0) : 'U'}</div>
                        <span className="dd-candidate-name">{row.candidate}</span>
                      </div>
                    </td>
                    <td className="dd-font-medium">{row.role}</td>
                    <td className="dd-text-muted">{row.project}</td>
                    <td>{getTypeBadge(row.type)}</td>
                    <td>{getStatusBadge(row.status)}</td>
                    <td>
                      {/* ✅ Primary Blue Button with White Eye Icon */}
                      <button
                        className="btn d-flex align-items-center justify-content-center"
                        style={{ 
                          width: '36px', 
                          height: '36px', 
                          borderRadius: '6px', 
                          padding: '0', 
                          backgroundColor: '#2563eb', 
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out'
                        }}
                        title="View Application"
                        onClick={() =>
                          navigate(`/dashboard/manage-applications/${row.id || row.roleApplicationId}`)
                        }
                      >
                        <Eye size={18} color="#ffffff" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DirectorDashboard;